import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/Popover';
import { useConfig } from './ConfigProvider';

import { useState } from 'react';
import { useNotificationRead } from './NotificationReadContext';
import { formatRelativeTimeKorean } from '@/utils/formatRelativeTimeKorea';
import { NAVIGATE_MAPPING_ALARM } from './_const/constants';
import { useNavigate } from 'react-router-dom';

export const AlertProvider = ({ children }: { children: React.ReactNode }) => {
  const { notificationList } = useConfig();
  const { readSeqNos, markAllAsRead } = useNotificationRead();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const getAlarmMapping = (title: string) => {
    // 1. endsWith + trim
    let matchedKey = Object.keys(NAVIGATE_MAPPING_ALARM).find((key) =>
      title.trim().endsWith(key.trim()),
    );
    if (matchedKey) return NAVIGATE_MAPPING_ALARM[matchedKey];

    // 2. includes (포함)로 fallback
    matchedKey = Object.keys(NAVIGATE_MAPPING_ALARM).find((key) => title.includes(key));
    if (matchedKey) return NAVIGATE_MAPPING_ALARM[matchedKey];

    return undefined;
  };

  // AlertProvider 내부
  const handleNotificationClick = (n) => {
    const mapping = getAlarmMapping(n.Title);
    if (mapping) {
      if (window.location.pathname + window.location.search === mapping.goal) {
        window.location.reload();
      } else {
        navigate(mapping.goal);
      }
    }
  };

  const unreadNotifications = notificationList.filter((n) => !readSeqNos.includes(n.SeqNo));

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          markAllAsRead(notificationList.map((n) => n.SeqNo));
        }
        setOpen(nextOpen);
      }}
    >
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-[300px] bg-white px-0 py-[20px]">
        <div className="flex flex-col gap-[16px]">
          <div className="px-[20px] Body1S16">알림</div>
          {notificationList.length === 0 ? (
            <div className="px-[20px] text-xs text-gr-500">새로운 알림이 없습니다.</div>
          ) : (
            <ul className="scrollbar flex max-h-[300px] flex-col overflow-auto px-[4px] ">
              {notificationList.map((n) => {
                const mapping = getAlarmMapping(n.Title);
                return (
                  <li
                    key={n.SeqNo}
                    className={`flex items-start gap-[12px] rounded-md px-[16px] py-[12px] hover:bg-gr-100 ${
                      mapping ? 'cursor-pointer' : 'cursor-default'
                    }`}
                    onClick={mapping ? () => handleNotificationClick(n) : undefined}
                  >
                    {/* 아이콘 + 빨간 점 */}
                    <div className="relative flex h-[32px] w-[32px] flex-shrink-0 items-center justify-center rounded-full bg-gr-100">
                      <img src={n.IconImageUrl} alt="" className="h-[16px] w-[16px]" />
                      {unreadNotifications.find((u) => u.SeqNo === n.SeqNo) && (
                        <span
                          className="absolute right-0 top-0 block h-[10px] w-[10px] rounded-full border-2 border-white bg-red-500"
                          style={{ transform: 'translate(15%,-15%)' }}
                        />
                      )}
                    </div>
                    {/* 텍스트 영역 */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <span className="truncate text-gr-500 Caption1M12 ">{n.Title}</span>
                        <span className=" whitespace-nowrap text-gr-500 Caption1M12">
                          {formatRelativeTimeKorean(n.RegisterDate)}
                        </span>
                      </div>
                      <div className="break-all  text-gr-800  Body5M14 ">{n.Content}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
